import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Check, Crown, Sparkles, Star, Zap, BookOpen, 
  MessageSquare, BarChart3, Users, Music, Loader2,
  ChevronLeft, Gift
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { OposaiLogo } from '@/components/TuPlazaIALogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanSelector = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [professionPricing, setProfessionPricing] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load system status, plans, and check access
      const [statusRes, plansRes, accessRes] = await Promise.all([
        axios.get(`${API}/system/status`),
        axios.get(`${API}/plans/all`),
        axios.get(`${API}/system/check-access`, { headers }).catch(() => ({ data: {} }))
      ]);

      setSystemStatus(statusRes.data);
      setPlans(plansRes.data.plans || []);

      // If payments are disabled or user is admin/special, skip to dashboard
      const access = accessRes.data;
      if (!statusRes.data.payments_enabled || access.is_admin || access.is_special_user) {
        // Assign default plan and go to dashboard
        if (!access.is_admin && !access.is_special_user) {
          await assignFreePlan();
        }
        navigate('/dashboard');
        return;
      }

      // Load profession-specific pricing if user has selected profession
      if (user?.selected_profession) {
        try {
          const pricingRes = await axios.get(`${API}/system/profession-pricing/${user.selected_profession}`);
          if (pricingRes.data.has_custom_pricing) {
            setProfessionPricing(pricingRes.data.pricing);
          }
        } catch (e) {
          // Use default pricing
        }
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const assignFreePlan = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/user/assign-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      // Ignore - user might already have a plan
    }
  };

  const getPlanPrice = (plan) => {
    // Check if there's custom pricing for this profession
    if (professionPricing) {
      const customPrice = professionPricing.find(p => p.plan_id === plan.plan_id);
      if (customPrice) {
        return billingPeriod === 'yearly' 
          ? customPrice.price_yearly || customPrice.price_monthly * 10
          : customPrice.price_monthly;
      }
    }
    return billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly;
  };

  const subscribeToPlan = async (planId) => {
    if (planId === 'trial') {
      // Free trial - just assign and continue
      setSubscribing(true);
      try {
        await assignFreePlan();
        toast.success('¡Prueba gratuita activada!');
        navigate('/dashboard');
      } catch (error) {
        toast.error('Error activando prueba');
      } finally {
        setSubscribing(false);
      }
      return;
    }

    setSubscribing(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/plans/subscribe`, {
        plan_id: planId,
        billing_period: billingPeriod
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error iniciando suscripción');
    } finally {
      setSubscribing(false);
    }
  };

  const skipForNow = () => {
    // Allow skip only if system allows it
    assignFreePlan();
    navigate('/dashboard');
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'trial': return <Gift className="w-6 h-6" />;
      case 'basic': return <Zap className="w-6 h-6" />;
      case 'top': return <Star className="w-6 h-6" />;
      case 'premium': return <Crown className="w-6 h-6" />;
      default: return <Sparkles className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'trial': return 'from-slate-500 to-slate-600';
      case 'basic': return 'from-blue-500 to-blue-600';
      case 'top': return 'from-purple-500 to-purple-600';
      case 'premium': return 'from-amber-500 to-amber-600';
      default: return 'from-indigo-500 to-indigo-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Header */}
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-100">
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <OposaiLogo size={40} />
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Elige tu plan de preparación
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Selecciona el plan que mejor se adapte a tus necesidades de estudio
          </p>

          {/* Billing Toggle */}
          <div className="mt-8 inline-flex items-center gap-3 bg-slate-100 rounded-full p-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                billingPeriod === 'monthly' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-600'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                billingPeriod === 'yearly' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-600'
              }`}
            >
              Anual
              <span className="ml-1 text-xs text-emerald-600 font-semibold">-17%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {plans.map((plan) => {
            const price = getPlanPrice(plan);
            const isPopular = plan.plan_id === 'top';
            const isPremium = plan.plan_id === 'premium';

            return (
              <div
                key={plan.plan_id}
                className={`relative bg-white rounded-2xl border-2 p-6 transition-all hover:shadow-lg ${
                  selectedPlan === plan.plan_id 
                    ? 'border-indigo-500 shadow-lg' 
                    : isPopular 
                      ? 'border-purple-300' 
                      : 'border-slate-200'
                }`}
                onClick={() => setSelectedPlan(plan.plan_id)}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                    MÁS POPULAR
                  </div>
                )}

                {/* Plan Header */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getPlanColor(plan.plan_id)} flex items-center justify-center text-white mb-4`}>
                  {getPlanIcon(plan.plan_id)}
                </div>

                <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{plan.description}</p>

                {/* Price */}
                <div className="mb-6">
                  {price === 0 ? (
                    <div className="text-3xl font-bold text-slate-900">Gratis</div>
                  ) : (
                    <>
                      <div className="text-3xl font-bold text-slate-900">
                        {price}€
                        <span className="text-base font-normal text-slate-500">
                          /{billingPeriod === 'yearly' ? 'año' : 'mes'}
                        </span>
                      </div>
                      {billingPeriod === 'yearly' && (
                        <p className="text-sm text-emerald-600">
                          {(price / 12).toFixed(2)}€/mes
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Credits */}
                <div className="flex items-center gap-2 mb-4 p-2 bg-indigo-50 rounded-lg">
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm font-medium text-indigo-900">
                    {plan.credits_monthly?.toLocaleString()} créditos/mes
                  </span>
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {plan.features?.slice(0, 5).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Feature Icons */}
                <div className="flex gap-2 flex-wrap mb-6">
                  {plan.has_tutor_ia && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> Tutor IA
                    </span>
                  )}
                  {plan.has_flashcards && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs flex items-center gap-1">
                      <BookOpen className="w-3 h-3" /> Flashcards
                    </span>
                  )}
                  {plan.has_audios && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs flex items-center gap-1">
                      <Music className="w-3 h-3" /> Audios
                    </span>
                  )}
                </div>

                {/* CTA Button */}
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    subscribeToPlan(plan.plan_id);
                  }}
                  disabled={subscribing}
                  className={`w-full ${
                    isPremium 
                      ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' 
                      : isPopular
                        ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                        : ''
                  }`}
                  variant={!isPremium && !isPopular ? 'outline' : 'default'}
                >
                  {subscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : price === 0 ? (
                    'Empezar gratis'
                  ) : (
                    'Seleccionar plan'
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Skip Option */}
        {!systemStatus?.payments_enabled && (
          <div className="text-center">
            <button
              onClick={skipForNow}
              className="text-slate-500 hover:text-slate-700 text-sm underline"
            >
              Continuar sin plan por ahora
            </button>
          </div>
        )}

        {/* Trust badges */}
        <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
          <span>✓ Cancela cuando quieras</span>
          <span>✓ Pago seguro con Stripe</span>
          <span>✓ Soporte 24/7</span>
        </div>
      </div>
    </div>
  );
};

export default PlanSelector;
